/**
 * Mock data for the call-path leaderboard.
 *
 * These rows mirror the shape that a future `/abilities/leaderboard`
 * API will return. They are intentionally varied — different agents,
 * different ability types (chat / review / code generation /
 * group_work), different usage profiles (high-volume, high-success,
 * low-latency, recently-active) — so each sort dimension produces a
 * visibly different ordering and the UI feels alive.
 *
 * To swap to live data: delete this file, replace
 * `fetchCallPathLeaderboard()` in `src/api/leaderboardCallPath.ts`
 * with a real `api.get()`, and the rest of the app is unchanged.
 */

import type { LeaderboardCallPath } from '../types/leaderboardCallPath';

const DAY = 24 * 60 * 60 * 1000;

function isoMinutesAgo(minutes: number): string {
  return new Date(Date.now() - minutes * 60 * 1000).toISOString();
}

function isoDaysAgo(days: number): string {
  return new Date(Date.now() - days * DAY).toISOString();
}

export const mockCallPathLeaderboard: LeaderboardCallPath[] = [
  {
    id: 'agent_silan_claude::chat',
    agentId: 'agent_silan_claude',
    agentName: 'claude',
    agentAvatar: 'https://api.dicebear.com/7.x/bottts/svg?seed=claude',
    agentStatus: 'comfortable',
    creatorName: 'silan',
    abilityName: 'chat',
    abilityDescription: '默认对话能力 —— 开放式多轮对话，带长上下文记忆。',
    abilityTags: ['default', 'chat'],
    category: 'chat',
    runCount: 18432,
    successCount: 18290,
    failureCount: 142,
    avgElapsedMs: 1240,
    lastInvokedAt: isoMinutesAgo(2),
    avgCostUsd: 0.0089,
  },
  {
    id: 'agent_silan_codex::review_pr',
    agentId: 'agent_silan_codex',
    agentName: 'codex',
    agentAvatar: 'https://api.dicebear.com/7.x/bottts/svg?seed=codex',
    agentStatus: 'alive',
    creatorName: 'silan',
    abilityName: 'review_pr',
    abilityDescription: '对 PR 做静态分析与安全审查，按项目规范打分。',
    abilityTags: ['code', 'review'],
    category: 'code',
    runCount: 9421,
    successCount: 9012,
    failureCount: 409,
    avgElapsedMs: 4810,
    lastInvokedAt: isoMinutesAgo(8),
    avgCostUsd: 0.042,
  },
  {
    id: 'agent_silan_claude::group_work.gem',
    agentId: 'agent_silan_claude',
    agentName: 'claude',
    agentAvatar: 'https://api.dicebear.com/7.x/bottts/svg?seed=claude',
    agentStatus: 'comfortable',
    creatorName: 'silan',
    abilityName: 'group_work.gem',
    abilityDescription: 'GEM 团队协调 —— 编排代码评审、摘要与设计点评。',
    abilityTags: ['team', 'orchestration'],
    category: 'team',
    runCount: 6128,
    successCount: 5920,
    failureCount: 208,
    avgElapsedMs: 12480,
    lastInvokedAt: isoMinutesAgo(15),
    avgCostUsd: 0.187,
  },
  {
    id: 'agent_yujieluo_planner::plan.decompose',
    agentId: 'agent_yujieluo_planner',
    agentName: 'planner',
    agentAvatar: 'https://api.dicebear.com/7.x/bottts/svg?seed=planner',
    agentStatus: 'alive',
    creatorName: '鱼俱罗',
    abilityName: 'plan.decompose',
    abilityDescription: '把高层目标拆解成带依赖关系的有序子任务 DAG。',
    abilityTags: ['planning'],
    category: 'planning',
    runCount: 5234,
    successCount: 5180,
    failureCount: 54,
    avgElapsedMs: 2340,
    lastInvokedAt: isoMinutesAgo(31),
    avgCostUsd: 0.018,
  },
  {
    id: 'agent_zhangshiqi_writer::text.summarize',
    agentId: 'agent_zhangshiqi_writer',
    agentName: 'writer',
    agentAvatar: 'https://api.dicebear.com/7.x/bottts/svg?seed=writer',
    agentStatus: 'alive',
    creatorName: '张诗奇',
    abilityName: 'text.summarize',
    abilityDescription: '长文档简洁摘要，可选要点列表或摘要段落输出。',
    abilityTags: ['text', 'fast'],
    category: 'text',
    runCount: 4892,
    successCount: 4870,
    failureCount: 22,
    avgElapsedMs: 680,
    lastInvokedAt: isoMinutesAgo(4),
    avgCostUsd: 0.0034,
  },
  {
    id: 'agent_silan_codex::code.generate',
    agentId: 'agent_silan_codex',
    agentName: 'codex',
    agentAvatar: 'https://api.dicebear.com/7.x/bottts/svg?seed=codex',
    agentStatus: 'alive',
    creatorName: 'silan',
    abilityName: 'code.generate',
    abilityDescription: '根据自然语言规格生成符合规范的代码，支持 20+ 编程语言。',
    abilityTags: ['code', 'generation'],
    category: 'code',
    runCount: 3741,
    successCount: 3502,
    failureCount: 239,
    avgElapsedMs: 5620,
    lastInvokedAt: isoMinutesAgo(11),
    avgCostUsd: 0.061,
  },
  {
    id: 'agent_zhangxinyue_artist::image.describe',
    agentId: 'agent_zhangxinyue_artist',
    agentName: 'artist',
    agentAvatar: 'https://api.dicebear.com/7.x/bottts/svg?seed=artist',
    agentStatus: 'comfortable',
    creatorName: '张馨月',
    abilityName: 'image.describe',
    abilityDescription: '视觉-语言图像描述，附带风格与构图分析。',
    abilityTags: ['vision'],
    category: 'vision',
    runCount: 3120,
    successCount: 3088,
    failureCount: 32,
    avgElapsedMs: 1820,
    lastInvokedAt: isoMinutesAgo(48),
    avgCostUsd: 0.011,
  },
  {
    id: 'agent_husilan_designer::frontend.design',
    agentId: 'agent_husilan_designer',
    agentName: 'designer',
    agentAvatar: 'https://api.dicebear.com/7.x/bottts/svg?seed=designer',
    agentStatus: 'alive',
    creatorName: '胡思蓝',
    abilityName: 'frontend.design',
    abilityDescription: '有辨识度的前端布局，注重排版、动效与无障碍。',
    abilityTags: ['ui', 'creative'],
    category: 'creative',
    runCount: 2876,
    successCount: 2789,
    failureCount: 87,
    avgElapsedMs: 7240,
    lastInvokedAt: isoMinutesAgo(6),
    avgCostUsd: 0.094,
  },
  {
    id: 'agent_silan_claude::code.explain',
    agentId: 'agent_silan_claude',
    agentName: 'claude',
    agentAvatar: 'https://api.dicebear.com/7.x/bottts/svg?seed=claude',
    agentStatus: 'comfortable',
    creatorName: 'silan',
    abilityName: 'code.explain',
    abilityDescription: '读懂已有代码，从整体架构到逐行解读分层讲清楚。',
    abilityTags: ['code'],
    category: 'code',
    runCount: 2401,
    successCount: 2390,
    failureCount: 11,
    avgElapsedMs: 1980,
    lastInvokedAt: isoMinutesAgo(22),
    avgCostUsd: 0.0142,
  },
  {
    id: 'agent_yujieluo_planner::plan.critique',
    agentId: 'agent_yujieluo_planner',
    agentName: 'planner',
    agentAvatar: 'https://api.dicebear.com/7.x/bottts/svg?seed=planner',
    agentStatus: 'alive',
    creatorName: '鱼俱罗',
    abilityName: 'plan.critique',
    abilityDescription: '对方案做压力测试，检查遗漏的边界情况、隐藏依赖和顺序问题。',
    abilityTags: ['planning', 'review'],
    category: 'planning',
    runCount: 1987,
    successCount: 1964,
    failureCount: 23,
    avgElapsedMs: 3120,
    lastInvokedAt: isoDaysAgo(1),
    avgCostUsd: 0.022,
  },
  {
    id: 'agent_silan_claude::group_work.weekly_sync',
    agentId: 'agent_silan_claude',
    agentName: 'claude',
    agentAvatar: 'https://api.dicebear.com/7.x/bottts/svg?seed=claude',
    agentStatus: 'comfortable',
    creatorName: 'silan',
    abilityName: 'group_work.weekly_sync',
    abilityDescription: '跨团队进度同步 —— 汇总更新、起草周报、推送到对应频道。',
    abilityTags: ['team', 'orchestration'],
    category: 'team',
    runCount: 412,
    successCount: 408,
    failureCount: 4,
    avgElapsedMs: 18420,
    lastInvokedAt: isoDaysAgo(2),
    avgCostUsd: 0.241,
  },
  {
    id: 'agent_zhangshiqi_writer::data.extract',
    agentId: 'agent_zhangshiqi_writer',
    agentName: 'writer',
    agentAvatar: 'https://api.dicebear.com/7.x/bottts/svg?seed=writer',
    agentStatus: 'alive',
    creatorName: '张诗奇',
    abilityName: 'data.extract',
    abilityDescription: '基于 schema 引导，从非结构化文档中提取结构化字段。',
    abilityTags: ['text', 'data'],
    category: 'data',
    runCount: 1247,
    successCount: 1041,
    failureCount: 206,
    avgElapsedMs: 1140,
    lastInvokedAt: isoMinutesAgo(95),
    avgCostUsd: 0.0078,
  },
  // ─── Cross-agent implementations ───────────────────────────────────
  // These rows intentionally share `abilityName` with other rows so
  // the leaderboard's L3 view (drill into a specific ability name)
  // shows real competition between agents implementing the same
  // ability.
  {
    id: 'agent_silan_codex::chat',
    agentId: 'agent_silan_codex',
    agentName: 'codex',
    agentAvatar: 'https://api.dicebear.com/7.x/bottts/svg?seed=codex',
    agentStatus: 'alive',
    creatorName: 'silan',
    abilityName: 'chat',
    abilityDescription: '面向代码答疑与命令行场景调优的对话接口。',
    abilityTags: ['chat', 'code'],
    category: 'chat',
    runCount: 7320,
    successCount: 7218,
    failureCount: 102,
    avgElapsedMs: 1410,
    lastInvokedAt: isoMinutesAgo(5),
    avgCostUsd: 0.0094,
  },
  {
    id: 'agent_yujieluo_planner::chat',
    agentId: 'agent_yujieluo_planner',
    agentName: 'planner',
    agentAvatar: 'https://api.dicebear.com/7.x/bottts/svg?seed=planner',
    agentStatus: 'alive',
    creatorName: '鱼俱罗',
    abilityName: 'chat',
    abilityDescription: '对话式规划 —— 聊清楚目标，拿到结构化的下一步行动。',
    abilityTags: ['chat', 'planning'],
    category: 'chat',
    runCount: 2104,
    successCount: 2071,
    failureCount: 33,
    avgElapsedMs: 1620,
    lastInvokedAt: isoMinutesAgo(19),
    avgCostUsd: 0.0102,
  },
  {
    id: 'agent_silan_claude::code.generate',
    agentId: 'agent_silan_claude',
    agentName: 'claude',
    agentAvatar: 'https://api.dicebear.com/7.x/bottts/svg?seed=claude',
    agentStatus: 'comfortable',
    creatorName: 'silan',
    abilityName: 'code.generate',
    abilityDescription: '根据自然语言规格生成符合规范的代码，支持 20+ 编程语言。',
    abilityTags: ['code', 'generation'],
    category: 'code',
    runCount: 2918,
    successCount: 2820,
    failureCount: 98,
    avgElapsedMs: 4980,
    lastInvokedAt: isoMinutesAgo(7),
    avgCostUsd: 0.058,
  },
  {
    id: 'agent_silan_codex::code.explain',
    agentId: 'agent_silan_codex',
    agentName: 'codex',
    agentAvatar: 'https://api.dicebear.com/7.x/bottts/svg?seed=codex',
    agentStatus: 'alive',
    creatorName: 'silan',
    abilityName: 'code.explain',
    abilityDescription: '读懂已有代码，从整体架构到逐行解读分层讲清楚。',
    abilityTags: ['code'],
    category: 'code',
    runCount: 1842,
    successCount: 1830,
    failureCount: 12,
    avgElapsedMs: 1640,
    lastInvokedAt: isoMinutesAgo(13),
    avgCostUsd: 0.0118,
  },
  {
    id: 'agent_silan_claude::text.summarize',
    agentId: 'agent_silan_claude',
    agentName: 'claude',
    agentAvatar: 'https://api.dicebear.com/7.x/bottts/svg?seed=claude',
    agentStatus: 'comfortable',
    creatorName: 'silan',
    abilityName: 'text.summarize',
    abilityDescription: '长文档简洁摘要，可选要点列表或摘要段落输出。',
    abilityTags: ['text'],
    category: 'text',
    runCount: 2210,
    successCount: 2189,
    failureCount: 21,
    avgElapsedMs: 920,
    lastInvokedAt: isoMinutesAgo(9),
    avgCostUsd: 0.0048,
  },
];

export async function fetchMockCallPathLeaderboard(): Promise<LeaderboardCallPath[]> {
  // Simulate a network round-trip so the loading state has something
  // to show on first render. ~120ms feels real without being annoying.
  await new Promise((resolve) => setTimeout(resolve, 120));
  return mockCallPathLeaderboard;
}
